---
title: The VM shell
lede: Your own terminal inside the session's machine, separate from the agent.
order: 17
part: Run a task
---

A VM shell is a terminal in the same machine the agent works in, opened at the workspace root. Use it to run tests, read a file or check what is installed, without anything reaching the agent's conversation.

![A VM shell](media/dock-shell.webp)

### Opening shells

The **VM shell** tab of the dock (`⌥⌘J`) holds the first shell. **Open another VM shell** (the `+` after the tabs) adds one; with more than one, they are numbered and each has a `×` to close it. Shells belong to the session, so you find them again when you come back to it.

A parked session gets its shell back when its machine wakes. A stopped session has to be reopened first.

### What you see

On a micro-VM, the shell is the guest: exactly what the agent sees, and nothing typed here reaches your computer. On a session placed [on this computer](/placement), there is no guest, so the shell runs on your computer in the session's worktree.

### Files in and out

- Drop or paste files on the shell to upload them into the machine; their paths are typed at the prompt.
- A file name the shell printed (by `ls`, for example) is a link: hover it and click to save the file on your computer.
- `⌘⇧D` lists the files named on screen and downloads the one you pick.

### Asking a question in the shell

On a micro-VM, a line that reads as a sentence is not run. It goes to the session's agent as a separate conversation, and the answer prints in the shell.

```
$ why does the test runner skip the integration folder?
↳ Claude Code · conversation 3f2a… — click to open it
```

A line is taken as a question when its first word is not a command and it runs to three words without a flag, or when it ends with `?`. Anything starting with `sudo` or `doas`, and anything typed inside a heredoc or a loop always runs as a command. Click the conversation line to continue it in a tab of its own.

### When a host is refused

A micro-VM's network is sealed at boot. When something in it asks for a host outside the workspace's allowlist, Airlock shows **Refused by the machine** with the host name.

- **Allow** adds the host to the workspace's allowlist and moves the session to a new machine, which the stage says while it starts; the session reaches the host on ports 80 and 443, usually a few seconds later. The agents' conversations and your changes are kept, and an agent that was working carries on.
- **Keep blocked** leaves the machine as it is.

:::note
On a session placed on this computer, the same prompt reads **Allow** or **Block** and acts on that one connection, without a restart. See [Network](/network).
:::
